import React, { useEffect, useState, useMemo } from "react";
import { api } from "@/lib/api";
import AdminLayout from "@/components/AdminLayout";
import { toast } from "sonner";
import {
  Inbox, Search, Mail, Phone, ChevronRight, ChevronDown, CheckSquare, Square as SquareIcon,
  Truck, Package, PackageCheck, X, ShoppingCart, ExternalLink, Tag, Loader2,
} from "lucide-react";

const STATI = [
  { id: "nuovo", label: "Nuovo", color: "bg-amber-100 text-amber-900", icon: Inbox },
  { id: "in_lavorazione", label: "In lavorazione", color: "bg-blue-100 text-blue-900", icon: Package },
  { id: "in_arrivo", label: "In arrivo", color: "bg-purple-100 text-purple-900", icon: Truck },
  { id: "consegnato", label: "Consegnato", color: "bg-emerald-100 text-emerald-900", icon: PackageCheck },
  { id: "in_catalogo", label: "In catalogo", color: "bg-cyan-100 text-cyan-900", icon: ShoppingCart },
  { id: "annullato", label: "Annullato", color: "bg-red-100 text-red-900", icon: X },
];

const statoMeta = (id) => STATI.find((s) => s.id === id) || STATI[0];

export default function Prenotazioni() {
  const [quotes, setQuotes] = useState([]);
  const [loading, setLoading] = useState(true);
  const [search, setSearch] = useState("");
  const [statoFilter, setStatoFilter] = useState("");
  const [selected, setSelected] = useState(new Set());
  const [expandedId, setExpandedId] = useState(null);
  const [moveModal, setMoveModal] = useState(null);

  const fetchQuotes = async () => {
    setLoading(true);
    try {
      const r = await api.get("/quote-requests");
      setQuotes(r.data || []);
    } catch (e) {
      toast.error("Errore caricamento: " + (e.response?.data?.detail || e.message));
    } finally { setLoading(false); }
  };

  useEffect(() => { fetchQuotes(); }, []);

  const filtered = useMemo(() => {
    const q = search.toLowerCase().trim();
    return quotes.filter((p) => {
      if (statoFilter && (p.stato || "nuovo") !== statoFilter) return false;
      if (!q) return true;
      return [p.sku_madre, p.nome_cliente, p.email, p.telefono]
        .some((f) => (f || "").toString().toLowerCase().includes(q));
    });
  }, [quotes, search, statoFilter]);

  const counts = useMemo(() => {
    const c = {};
    STATI.forEach((s) => { c[s.id] = quotes.filter((q) => (q.stato || "nuovo") === s.id).length; });
    return c;
  }, [quotes]);

  const toggleSel = (id) => setSelected((s) =>
    new Set(s.has(id) ? [...s].filter((x) => x !== id) : [...s, id])
  );

  const updateStato = async (id, stato, extra = {}) => {
    try {
      await api.put(`/quote-requests/${id}`, { stato, ...extra });
      toast.success(`Stato aggiornato: ${statoMeta(stato).label}`);
      fetchQuotes();
    } catch (e) { toast.error(e.response?.data?.detail || "Errore"); }
  };

  const bulkMoveToCatalog = async (lane, stock_default) => {
    const ids = [...selected];
    if (!ids.length) return;
    try {
      const r = await api.post("/quote-requests/move-to-catalog", { ids, set_lane: lane, stock_default });
      toast.success(`Spostati ${r.data.created} in catalogo`);
      setSelected(new Set());
      setMoveModal(null);
      fetchQuotes();
    } catch (e) { toast.error(e.response?.data?.detail || "Errore"); }
  };

  if (loading) return <AdminLayout><div className="min-h-screen flex items-center justify-center"><Loader2 className="w-8 h-8 animate-spin" /></div></AdminLayout>;

  return (
    <AdminLayout>
      <div className="p-8 lg:p-10" data-testid="prenotazioni-page">
        <div className="mb-8">
          <div className="overline text-brand mb-2">CRM</div>
          <h1 className="font-heading font-bold text-4xl tracking-tight mb-1">Prenotazioni B2C</h1>
          <p className="text-sm text-muted-foreground">Tracking dei configuratori B2C: dal lead all&apos;ingresso a catalogo.</p>
        </div>

        {/* Pill counters */}
        <div className="flex flex-wrap gap-2 mb-6" data-testid="prenotazioni-stato-tabs">
          <button onClick={() => setStatoFilter("")}
            className={`px-3 py-1.5 rounded-sm text-xs border ${statoFilter === "" ? "bg-foreground text-white border-foreground" : "border-border hover:bg-accent"}`}>
            Tutti ({quotes.length})
          </button>
          {STATI.map((s) => (
            <button key={s.id} onClick={() => setStatoFilter(s.id)}
              data-testid={`prenotazioni-stato-${s.id}`}
              className={`px-3 py-1.5 rounded-sm text-xs border inline-flex items-center gap-1.5 ${statoFilter === s.id ? "bg-foreground text-white border-foreground" : "border-border hover:bg-accent"}`}>
              <s.icon className="w-3.5 h-3.5" /> {s.label} ({counts[s.id] || 0})
            </button>
          ))}
        </div>

        {/* Search */}
        <div className="bg-white border border-border rounded-sm p-3 mb-4">
          <div className="relative">
            <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-muted-foreground" />
            <input value={search} onChange={(e) => setSearch(e.target.value)} placeholder="Cerca per SKU, nome cliente, email…"
              data-testid="prenotazioni-search"
              className="w-full h-10 pl-9 pr-3 rounded-sm border border-border text-sm" />
          </div>
        </div>

        {/* Table */}
        <div className="bg-white border border-border rounded-sm overflow-hidden">
          <div className="grid grid-cols-12 gap-2 px-4 py-2 border-b border-border bg-neutral-50 text-[10px] uppercase tracking-wider text-muted-foreground font-medium">
            <div className="col-span-1"></div>
            <div className="col-span-2">SKU</div>
            <div className="col-span-3">Cliente</div>
            <div className="col-span-2">Selezioni</div>
            <div className="col-span-1">Prezzo</div>
            <div className="col-span-2">Stato</div>
            <div className="col-span-1">Azioni</div>
          </div>
          {filtered.length === 0 ? (
            <div className="px-4 py-16 text-center text-sm text-muted-foreground">
              <Inbox className="w-12 h-12 mx-auto mb-3 opacity-30" />
              Nessuna prenotazione {statoFilter ? `nello stato "${statoMeta(statoFilter).label}"` : "ancora"}
            </div>
          ) : filtered.map((q) => {
            const stato = q.stato || "nuovo";
            const meta = statoMeta(stato);
            const isExp = expandedId === q.id;
            const isSel = selected.has(q.id);
            return (
              <div key={q.id} className={`border-b border-border last:border-0 ${isSel ? "bg-brand/5" : ""}`} data-testid={`prenotazione-${q.id}`}>
                <div className="grid grid-cols-12 gap-2 px-4 py-3 items-center text-sm">
                  <div className="col-span-1">
                    <button onClick={() => toggleSel(q.id)} data-testid={`prenotazione-chk-${q.id}`}
                      className="w-5 h-5 inline-flex items-center justify-center">
                      {isSel ? <CheckSquare className="w-4 h-4 text-brand" /> : <SquareIcon className="w-4 h-4 text-muted-foreground" />}
                    </button>
                  </div>
                  <div className="col-span-2 font-mono text-xs">{q.sku_madre}</div>
                  <div className="col-span-3">
                    <div className="font-medium text-sm">{q.nome_cliente || <span className="text-muted-foreground italic">Anonimo</span>}</div>
                    <div className="text-[11px] text-muted-foreground flex items-center gap-2">
                      {q.email && <span className="inline-flex items-center gap-1"><Mail className="w-3 h-3" />{q.email}</span>}
                      {q.telefono && <span className="inline-flex items-center gap-1"><Phone className="w-3 h-3" />{q.telefono}</span>}
                    </div>
                  </div>
                  <div className="col-span-2 text-[11px] text-muted-foreground">
                    {q.selezioni?.tessuto?.codice && <div>🧶 {q.selezioni.tessuto.codice}-{q.selezioni.tessuto.codice_colore}</div>}
                    {q.selezioni?.piede?.codice && <div>🦵 {q.selezioni.piede.codice}</div>}
                    {q.selezioni?.metallo?.codice && <div>✨ {q.selezioni.metallo.codice}</div>}
                  </div>
                  <div className="col-span-1 font-medium">€ {(q.prezzo_stimato || 0).toFixed(2)}</div>
                  <div className="col-span-2">
                    <select value={stato} onChange={(e) => updateStato(q.id, e.target.value)}
                      data-testid={`prenotazione-stato-sel-${q.id}`}
                      className={`text-[11px] font-medium px-2 py-1 rounded-sm border-0 cursor-pointer ${meta.color}`}>
                      {STATI.map((s) => <option key={s.id} value={s.id}>{s.label}</option>)}
                    </select>
                  </div>
                  <div className="col-span-1">
                    <button onClick={() => setExpandedId(isExp ? null : q.id)}
                      data-testid={`prenotazione-toggle-${q.id}`}
                      className="p-1 hover:bg-accent rounded-sm">
                      {isExp ? <ChevronDown className="w-4 h-4" /> : <ChevronRight className="w-4 h-4" />}
                    </button>
                  </div>
                </div>
                {isExp && (
                  <div className="px-4 pb-4 pt-2 bg-neutral-50 border-t border-border" data-testid={`prenotazione-detail-${q.id}`}>
                    <div className="grid grid-cols-3 gap-4">
                      <div>
                        <div className="text-[10px] uppercase tracking-wider text-muted-foreground mb-1">Tracking</div>
                        <input value={q.tracking_code || ""} placeholder="Codice tracking"
                          onChange={(e) => updateStato(q.id, q.stato, { tracking_code: e.target.value })}
                          className="w-full h-8 text-xs border border-border rounded-sm px-2 mb-1"
                          data-testid={`prenotazione-tracking-${q.id}`} />
                        <input value={q.tracking_url || ""} placeholder="URL tracking (es. https://...)"
                          onChange={(e) => updateStato(q.id, q.stato, { tracking_url: e.target.value })}
                          className="w-full h-8 text-xs border border-border rounded-sm px-2" />
                      </div>
                      <div>
                        <div className="text-[10px] uppercase tracking-wider text-muted-foreground mb-1">Storia stati</div>
                        <div className="text-[11px] space-y-0.5 max-h-24 overflow-auto">
                          {(q.history || []).map((h, i) => (
                            <div key={i}>{h.at?.slice(0, 16).replace("T", " ")} → <strong>{statoMeta(h.stato).label}</strong></div>
                          ))}
                          {!q.history?.length && <div className="text-muted-foreground italic">Nessuno storico</div>}
                        </div>
                      </div>
                      <div>
                        <div className="text-[10px] uppercase tracking-wider text-muted-foreground mb-1">Link tracking pubblico</div>
                        <a href={`/tracking/${q.id}`} target="_blank" rel="noopener noreferrer"
                          className="text-xs text-brand hover:underline inline-flex items-center gap-1"
                          data-testid={`prenotazione-public-link-${q.id}`}>
                          /tracking/{q.id.slice(0, 8)} <ExternalLink className="w-3 h-3" />
                        </a>
                        {q.linked_realized_id && (
                          <div className="mt-2 text-[11px] text-emerald-700 inline-flex items-center gap-1">
                            <ShoppingCart className="w-3 h-3" /> Già in catalogo
                          </div>
                        )}
                      </div>
                    </div>
                  </div>
                )}
              </div>
            );
          })}
        </div>

        {/* Bulk bar */}
        {selected.size > 0 && (
          <div className="fixed bottom-4 left-1/2 -translate-x-1/2 z-40 bg-foreground text-white rounded-sm shadow-2xl px-4 py-2.5 flex items-center gap-2" data-testid="prenotazioni-bulk-bar">
            <span className="text-xs font-medium pr-2 border-r border-white/20">{selected.size} selezionati</span>
            <button onClick={() => setMoveModal({ ids: [...selected] })} data-testid="prenotazioni-bulk-move"
              className="inline-flex items-center gap-1.5 px-3 py-1.5 rounded-sm text-xs hover:bg-white/10">
              <ShoppingCart className="w-3.5 h-3.5" /> Sposta in catalogo
            </button>
            <button onClick={async () => {
              for (const id of selected) await api.put(`/quote-requests/${id}`, { stato: "in_arrivo" }).catch(() => {});
              toast.success("Stati aggiornati");
              setSelected(new Set());
              fetchQuotes();
            }} data-testid="prenotazioni-bulk-arrivo"
              className="inline-flex items-center gap-1.5 px-3 py-1.5 rounded-sm text-xs hover:bg-white/10">
              <Truck className="w-3.5 h-3.5" /> Segna &quot;in arrivo&quot; + email
            </button>
            <button onClick={() => setSelected(new Set())} className="ml-2 text-xs text-white/60 hover:text-white">Annulla</button>
          </div>
        )}

        {/* Move to catalog modal */}
        {moveModal && (
          <MoveToCatalogModal ids={moveModal.ids} onClose={() => setMoveModal(null)}
            onApply={(lane, stock) => bulkMoveToCatalog(lane, stock)} />
        )}
      </div>
    </AdminLayout>
  );
}

function MoveToCatalogModal({ ids, onClose, onApply }) {
  const [lane, setLane] = useState("pronta_consegna");
  const [stock, setStock] = useState(1);
  return (
    <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50" data-testid="move-to-catalog-modal">
      <div className="bg-white rounded-sm p-6 max-w-md w-full mx-4 shadow-xl">
        <h3 className="text-lg font-bold mb-1 inline-flex items-center gap-2"><ShoppingCart className="w-5 h-5" /> Sposta in catalogo</h3>
        <p className="text-xs text-muted-foreground mb-4">Crea {ids.length} SKU realizzato/i partendo dalle prenotazioni selezionate.</p>
        <div className="space-y-3">
          <div>
            <label className="text-xs uppercase tracking-wider text-muted-foreground">Disponibilità</label>
            <select value={lane} onChange={(e) => setLane(e.target.value)} className="w-full h-10 border border-border rounded-sm px-2 text-sm" data-testid="move-lane">
              <option value="pronta_consegna">🟢 Pronta consegna</option>
              <option value="ordinabile_breve">🟡 Ordinabile breve (3-5gg)</option>
              <option value="prenotabile_lungo">🔵 Prenotabile (40-70gg)</option>
            </select>
          </div>
          <div>
            <label className="text-xs uppercase tracking-wider text-muted-foreground">Stock iniziale</label>
            <input type="number" value={stock} onChange={(e) => setStock(parseInt(e.target.value) || 0)}
              className="w-full h-10 border border-border rounded-sm px-2 text-sm" data-testid="move-stock" />
          </div>
        </div>
        <div className="flex gap-2 mt-5">
          <button onClick={onClose} className="flex-1 h-10 border border-border rounded-sm text-sm">Annulla</button>
          <button onClick={() => onApply(lane, stock)} className="flex-1 h-10 bg-brand text-white rounded-sm text-sm font-medium" data-testid="move-apply">
            <Tag className="w-4 h-4 inline mr-1" /> Sposta in catalogo
          </button>
        </div>
      </div>
    </div>
  );
}
